import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, useUser } from "@clerk/clerk-react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faChartLine } from '@fortawesome/free-solid-svg-icons';

function HomePage() {
  const { isSignedIn, signOut } = useAuth();
  const { user } = useUser();

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-hidden">
      {/* Modern background elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.3),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,119,198,0.3),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_40%_40%,rgba(120,200,255,0.2),transparent_50%)]"></div>
      {/* Mobile Layout (Ultra Modern App Style) */}
      <div className="md:hidden h-full bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 overflow-y-auto">
        {/* Smart Mobile Header */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b border-white/20 shadow-lg"
        >
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              {/* App Logo/Title */}
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
                  <span className="text-xl">🌙</span>
                </div>
                <div>
                  <h1 className="text-lg font-bold text-gray-800">Arabic Learning</h1>
                  <p className="text-xs text-gray-500">
                    {isSignedIn ? `Welcome back, ${user?.firstName || 'User'}!` : 'Welcome! Sign in to track progress'}
                  </p>
                </div>
              </div>

              {/* User Actions */}
              <div className="flex items-center space-x-2">
                {isSignedIn ? (
                  <>
                    {/* Dashboard Button */}
                    <Link
                      to="/dashboard"
                      className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                      title="Dashboard"
                    >
                      <FontAwesomeIcon icon={faChartLine} className="text-white text-sm" />
                    </Link>
                    
                    
                    {/* Sign Out Button */}
                    <button
                      onClick={() => signOut()}
                      className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                      title="Sign Out"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} className="text-white text-sm" />
                    </button>
                  </>
                ) : (
                  <>
                    {/* Login Button */}
                    <Link
                      to="/login"
                      className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    >
                      Login
                    </Link>
                    
                    {/* Sign Up Button */}
                    <Link
                      to="/signup"
                      className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-sm font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Hero Section */}
        <div className="relative px-6 pt-6 pb-6">
          {/* Background Elements */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(120,119,198,0.15),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(255,119,198,0.15),transparent_50%)]"></div>
          
          {/* Daily Dua - Top Priority */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative z-10 mb-8"
          >
            <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-3xl p-6 text-white shadow-2xl border border-white/20">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                  <span className="text-2xl">🤲</span>
                </div>
                <h2 className="font-bold text-xl">Daily Dua</h2>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold mb-3" dir="rtl">رَبِّ زِدْنِي عِلْمًا</div>
                <div className="text-orange-100 font-medium mb-2">Rabbi zidni ilma</div>
                <div className="text-white/90 italic text-sm">My Lord, increase me in knowledge</div>
              </div>
            </div>
          </motion.div>


          {/* Quick Action Cards */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-2 gap-4 mb-8"
          >
            <Link
              to="/lessons"
              className="group bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/30 hover:shadow-xl transition-all duration-300 active:scale-95"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">📚</span>
                </div>
                <h3 className="font-bold text-gray-800 text-base mb-1">Lessons</h3>
                <p className="text-xs text-gray-500">Start learning</p>
              </div>
            </Link>
            
            <Link
              to="/game"
              className="group bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-lg border border-white/30 hover:shadow-xl transition-all duration-300 active:scale-95"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">🎮</span>
                </div>
                <h3 className="font-bold text-gray-800 text-base mb-1">Games</h3>
                <p className="text-xs text-gray-500">Play & learn</p>
              </div>
            </Link>
          </motion.div>

          {/* Progress Card */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-3xl p-6 text-white shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Your Learning Journey</h3>
              <div className="text-3xl font-bold">0%</div>
            </div>
            <div className="bg-white/20 rounded-full h-3 mb-4 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "0%" }}
                transition={{ duration: 1, delay: 0.8 }}
                className="bg-white rounded-full h-3"
              />
            </div>
            <p className="text-sm opacity-90">Complete your first lesson to start tracking progress</p>
          </motion.div>

          {/* Featured Learning Paths */}
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-4 mt-8"
          >
            <h2 className="text-xl font-bold text-gray-800 mb-4">Popular Learning Paths</h2>
            
            <div className="space-y-3">
              <Link
                to="/alphabet-lesson"
                className="group bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-md border border-white/40 hover:shadow-lg transition-all duration-300 active:scale-98 flex items-center space-x-4"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl">🔤</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-800 text-base mb-1">Arabic Alphabet</h3>
                  <p className="text-sm text-gray-500">Master the 28 letters</p>
                </div>
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-blue-100 transition-colors duration-300">
                  <span className="text-gray-400 text-sm group-hover:text-blue-500">→</span>
                </div>
              </Link>

              <Link
                to="/word-lesson"
                className="group bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-md border border-white/40 hover:shadow-lg transition-all duration-300 active:scale-98 flex items-center space-x-4"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-teal-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl">📝</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-800 text-base mb-1">Arabic Words</h3>
                  <p className="text-sm text-gray-500">Essential vocabulary</p>
                </div>
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-green-100 transition-colors duration-300">
                  <span className="text-gray-400 text-sm group-hover:text-green-500">→</span>
                </div>
              </Link>

              <Link
                to="/surah-list"
                className="group bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-md border border-white/40 hover:shadow-lg transition-all duration-300 active:scale-98 flex items-center space-x-4"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl">📖</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-800 text-base mb-1">Quran Surahs</h3>
                  <p className="text-sm text-gray-500">Learn & memorize</p>
                </div>
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-purple-100 transition-colors duration-300">
                  <span className="text-gray-400 text-sm group-hover:text-purple-500">→</span>
                </div>
              </Link>

              <Link
                to="/prayer-lessons"
                className="group bg-white/90 backdrop-blur-sm rounded-2xl p-5 shadow-md border border-white/40 hover:shadow-lg transition-all duration-300 active:scale-98 flex items-center space-x-4"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <span className="text-2xl">🕌</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-800 text-base mb-1">Prayer Lessons</h3>
                  <p className="text-sm text-gray-500">Learn Salah step by step</p>
                </div>
                <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center group-hover:bg-orange-100 transition-colors duration-300">
                  <span className="text-gray-400 text-sm group-hover:text-orange-500">→</span>
                </div>
              </Link>
            </div>
          </motion.div>

        </div>
      </div>

      {/* Desktop Layout (enhanced) */}
      <div className="hidden md:block">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-200/20 to-pink-200/20"></div>
          <div className="relative max-w-7xl mx-auto px-6 py-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Left: Content */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center lg:text-left"
              >
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="text-8xl mb-6 relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-300 to-orange-300 rounded-full blur-2xl opacity-40"></div>
                  <div className="relative z-10">🌙</div>
                </motion.div>
                <h1 className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent font-[Comic Sans MS,cursive] mb-6 drop-shadow-sm">
                  Welcome to Arabic Learning Hub
                </h1>
                <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                  A fun & friendly world where kids learn Arabic, Surahs, Prayers, and Stories of the Prophets! 🌟
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Link
                    to="/lessons"
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full text-xl font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                  >
                    🚀 Start Learning
                  </Link>
                  <Link
                    to="/game"
                    className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-8 py-4 rounded-full text-xl font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-105"
                  >
                    🎮 Play Games
                  </Link>
                </div>
              </motion.div>

              {/* Right: Feature Cards */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
                className="grid grid-cols-2 gap-6"
              >
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5 }}
                  className="bg-white/70 backdrop-blur-xl p-6 rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-4xl mb-3">📚</div>
                  <h3 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">Lessons</h3>
                  <p className="text-sm text-gray-600">Learn Arabic letters, words, and pronunciation</p>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5 }}
                  className="bg-white/70 backdrop-blur-xl p-6 rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-4xl mb-3">🎮</div>
                  <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">Games</h3>
                  <p className="text-sm text-gray-600">Fun interactive games to reinforce learning</p>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5 }}
                  className="bg-white/70 backdrop-blur-xl p-6 rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-4xl mb-3">🕌</div>
                  <h3 className="text-lg font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-2">Prayer</h3>
                  <p className="text-sm text-gray-600">Learn Islamic prayers and practices</p>
                </motion.div>
                <motion.div
                  whileHover={{ scale: 1.05, rotateY: 5 }}
                  className="bg-white/70 backdrop-blur-xl p-6 rounded-2xl shadow-xl border border-white/30 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="text-4xl mb-3">📖</div>
                  <h3 className="text-lg font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent mb-2">Stories</h3>
                  <p className="text-sm text-gray-600">Prophet stories and Islamic teachings</p>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Quick Access Section */}
        <div className="max-w-7xl mx-auto px-6 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent mb-4">Quick Access</h2>
            <p className="text-xl text-gray-600">Jump into your favorite activities</p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              whileHover={{ scale: 1.05, rotateY: 5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/alphabet-lesson"
                className="block bg-gradient-to-br from-purple-500 via-pink-500 to-purple-600 text-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/20"
              >
                <div className="text-5xl mb-4">🔤</div>
                <h3 className="text-xl font-bold mb-2">Alphabet</h3>
                <p className="text-sm opacity-90">Learn Arabic letters</p>
              </Link>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05, rotateY: 5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/flashcards"
                className="block bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 text-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/20"
              >
                <div className="text-5xl mb-4">🧠</div>
                <h3 className="text-xl font-bold mb-2">Flashcards</h3>
                <p className="text-sm opacity-90">Memory practice</p>
              </Link>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05, rotateY: 5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/prophet-stories"
                className="block bg-gradient-to-br from-green-500 via-blue-500 to-purple-500 text-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/20"
              >
                <div className="text-5xl mb-4">📚</div>
                <h3 className="text-xl font-bold mb-2">Stories</h3>
                <p className="text-sm opacity-90">Prophet tales</p>
              </Link>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.05, rotateY: 5 }}
              whileTap={{ scale: 0.95 }}
            >
              <Link
                to="/kids-songs"
                className="block bg-gradient-to-br from-pink-500 via-purple-500 to-blue-500 text-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/20"
              >
                <div className="text-5xl mb-4">🎵</div>
                <h3 className="text-xl font-bold mb-2">Songs</h3>
                <p className="text-sm opacity-90">Kids' songs</p>
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default HomePage;
